import { useEffect, useMemo, useState } from 'react'

import { Table } from '@loadsmart/loadsmart-ui'
import { Text, Layout } from '@loadsmart/miranda-react'
import type { ChangeEvent } from '@loadsmart/miranda-wc/dist/utils/types'

import { parseCurrencyValue } from 'core/currency/presenter/CurrencyPresenter'
import { CurrencyField } from 'core/currency/ui/CurrencyField'
import { formatRate } from 'core/presenters/laneRatePresenter'
import { isEmpty } from 'core/utils/array'

import {
  type AccessorialCharge,
  getAllInRate,
  type QuoteAccessorial,
  getAccessorialCharges,
  type AccessorialHistoryCharge,
} from '../domain/QuoteAccessorial'
import { type RateLine } from '../domain/RateLine'
import {
  formatAccessorialChargeKeyAndName,
  getAccessorialNames,
} from '../presenter/quoteAccessorialPresenter'
import { StyledTextField, TableWrapper, AllInRateGroup } from './ShipperAccessorialCharges.styles'

export type ShipperAccessorialChargesProps = {
  quoteAccessorials: Array<QuoteAccessorial> | undefined
  accessorialsHistory: Array<AccessorialHistoryCharge> | undefined
  flatRate: number | undefined
  onAccessorialsChange: (accessorials: Array<AccessorialCharge>, rateUUID?: string) => void
  isSubmitted: boolean
  ratelines: Array<RateLine> | undefined
  rateUUID: string | undefined
  disabled: boolean | undefined
}

export function ShipperAccessorialCharges({
  quoteAccessorials,
  accessorialsHistory,
  ratelines,
  flatRate,
  onAccessorialsChange,
  isSubmitted,
  rateUUID,
  disabled,
}: ShipperAccessorialChargesProps) {
  const accessorialsAreEmpty = isEmpty(quoteAccessorials)

  const accessorialNames = getAccessorialNames(quoteAccessorials)
  const [accessorialCharges, setAccessorialCharges] = useState<Array<AccessorialCharge>>([])

  useEffect(() => {
    const commonChargesParameters = {
      ratelines: ratelines ?? [],
      accessorialsHistory: accessorialsHistory ?? [],
      isSubmitted,
    }
    const hasEmptyRatelines = isEmpty(commonChargesParameters.ratelines)

    if (hasEmptyRatelines && isSubmitted) return

    const newCharges = getAccessorialCharges({ quoteAccessorials, ...commonChargesParameters })

    setAccessorialCharges(newCharges)
  }, [ratelines, accessorialsHistory, isSubmitted, quoteAccessorials])

  useEffect(() => {
    onAccessorialsChange(accessorialCharges, rateUUID)
  }, [accessorialCharges, onAccessorialsChange, rateUUID])

  function handleAccessorialRateChange(index: number, event: ChangeEvent<HTMLInputElement>) {
    let updatedAccessorialsList = accessorialCharges.map(a => {
      return { ...a }
    })

    updatedAccessorialsList[index].rate = parseCurrencyValue(event.target.value)
    setAccessorialCharges(updatedAccessorialsList)
  }

  function handleAccessorialNotesChange(index: number, event: React.ChangeEvent<HTMLInputElement>) {
    let updatedAccessorialsList = accessorialCharges.map(a => {
      return { ...a }
    })

    updatedAccessorialsList[index].notes = event.target.value
    setAccessorialCharges(updatedAccessorialsList)
  }

  const allInRate = useMemo(() => {
    return formatRate(getAllInRate(accessorialCharges, flatRate), true)
  }, [flatRate, accessorialCharges])

  if (accessorialsAreEmpty) {
    return null
  }

  return (
    <Layout.Stack gap="spacing-1">
      <Text variant="heading-sm-bold">Shipper requested accessorial charges</Text>
      <Text>
        You can split your final quote rate per accessorial charge this may increase your chance to
        close business.
      </Text>
      <TableWrapper>
        <Table>
          <Table.Head>
            <Table.Row>
              <Table.HeadCell>Accessorial Charges</Table.HeadCell>
              <Table.HeadCell>Unit</Table.HeadCell>
              <Table.HeadCell>Rate</Table.HeadCell>
              <Table.HeadCell>Notes</Table.HeadCell>
            </Table.Row>
          </Table.Head>

          <Table.Body>
            {accessorialCharges.map((accessorialCharge, index) => {
              const { key, name } = formatAccessorialChargeKeyAndName(
                accessorialCharge,
                accessorialNames
              )

              const rateLabelId = `accessorial-rate-label-${key}`

              return (
                <Table.Row key={key}>
                  <Table.Cell>
                    <Text>{name}</Text>
                  </Table.Cell>
                  <Table.Cell style={{ width: '8%' }}>
                    <Text>{accessorialCharge.unit}</Text>
                  </Table.Cell>
                  <Table.Cell style={{ width: '10%' }}>
                    <span id={rateLabelId} hidden>
                      Rate
                    </span>
                    <CurrencyField
                      aria-labelledby={rateLabelId}
                      onChange={event => handleAccessorialRateChange(index, event)}
                      value={accessorialCharge.rate ?? ''}
                      disabled={disabled}
                    />
                  </Table.Cell>
                  <Table.Cell style={{ width: '60%' }}>
                    <StyledTextField
                      name="notes"
                      scale="small"
                      maxLength={100}
                      onChange={event => handleAccessorialNotesChange(index, event)}
                      value={accessorialCharge.notes}
                      disabled={disabled}
                    />
                  </Table.Cell>
                </Table.Row>
              )
            })}
          </Table.Body>
        </Table>
      </TableWrapper>
      <AllInRateGroup>
        <Text variant="body-md-bold">All in rate (Flat rate + accessorials)</Text>
        <Text variant="heading-sm-bold" data-testid="all-in-rate-price">
          ${allInRate}
        </Text>
      </AllInRateGroup>
    </Layout.Stack>
  )
}
